import { useQuery } from "@tanstack/react-query";
import { Link } from "@bloomberg/media-ui-core";
import React, { useCallback, useMemo } from "react";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./styles.module.css";
import LazyRetinaImage from "~components/LazyRetinaImage";
import { fetchMarketWrapNews } from "~components/MarketsSummary/util";
import { MarketsWrapNewsArticle } from "~types/types";
import { SECTION_FRONT_REFRESH_INTERVAL_IN_MS } from "~constants";
import { getRelativeTime } from "~utils/dateTimeUtils";
import { MarketSummariesPlaceholder } from "~components/MarketsSummary/MarketSummariesPlaceholder";
import { PlaceholderWithError } from "~components/PlaceholderWithError";
import { addPageAction, logError } from "~utils/newrelic/newrelicBrowser";
import { useTranslateMessage } from "~utils/i18n";

const IMAGE_DOMAIN = process.env.NEXT_PUBLIC_ASSET_HOST ?? "assets.bwbx.io";
const IMAGE_ASSET_PREFIX = `https://${IMAGE_DOMAIN}/s3/bazaar/images`;
const DEFAULT_MARKET_SUMMARY_IMAGE_URL = `${IMAGE_ASSET_PREFIX}/market_summary.png`;

type MarketSummaryProps = {
  sectionFrontName: string;
};

export default function MarketsSummary(props: MarketSummaryProps) {
  const { sectionFrontName } = props;

  const {
    data: article,
    status,
    refetch,
  } = useQuery<MarketsWrapNewsArticle | null, Error>({
    queryKey: ["marketsWrapNews", sectionFrontName],
    queryFn: () => fetchMarketWrapNews(sectionFrontName),
    // 5min stale time
    staleTime: SECTION_FRONT_REFRESH_INTERVAL_IN_MS,
  });

  const translate = useTranslateMessage();

  // to avoid hydration errors
  const articleData = useMemo(() => {
    if (!article) {
      return undefined;
    }

    const { headline, mainMoves, thumbnail, updatedAt, url } = article;

    const articleUpdatedTime = updatedAt ? getRelativeTime(updatedAt) : "";

    const imageProps = {
      baseUrl: thumbnail?.baseUrl || DEFAULT_MARKET_SUMMARY_IMAGE_URL,
    };

    return { articleUpdatedTime, headline, imageProps, mainMoves, url };
  }, [article]);

  const onClick = useCallback(() => {
    trackClickEvent("Market_Data", "market_summaries", "click", "Market Data");
    addPageAction("marketSummariesClick");
  }, []);

  if (status === "pending") {
    return <MarketSummariesPlaceholder />;
  }

  if (status === "error" || !articleData) {
    logError(new Error("Market Summaries Fetch Error"));

    return (
      <div className={styles.newsletter}>
        <PlaceholderWithError
          errorMessage={translate({
            id: "errMarketSummaries",
            defaultMessage: "There was an error loading Market Summaries.",
          })}
          placeholder={<MarketSummariesPlaceholder />}
          onRetryClick={() => refetch({ throwOnError: true })}
        />
      </div>
    );
  }

  const { articleUpdatedTime, headline, imageProps, mainMoves, url } =
    articleData;

  return (
    <section className={styles.marketSummaries}>
      <div className={styles.sectionTitle}>Market Summaries</div>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <Link
            href={url}
            onClick={onClick}
            target="_blank"
            className={styles.headerImage}
          >
            <LazyRetinaImage
              image={imageProps}
              desktopMaxWidth={72}
              mobileMaxWidth={72}
              aspectRatio={1}
            />
          </Link>
          <div className={styles.headerTitle}>
            <div className={styles.title}>
              <span>Markets Wrap</span>
              <span>|</span>
              <span>{articleUpdatedTime}</span>
            </div>
            <Link href={url} onClick={onClick} target="_blank" unstyled>
              <div className={styles.subTitle}>{headline}</div>
            </Link>
          </div>
        </div>
        <div className={styles.marketMovesContainer}>
          {mainMoves?.length > 0 && (
            <ul className={styles.summaryList}>
              {mainMoves.map((item, index) => (
                <li key={index} className={styles.summaryItem}>
                  <Link href={url} target="_blank" unstyled onClick={onClick}>
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
